/**
 * A theme-token picker for a format rule's text or fill colour.
 *
 * **The island's own, not the web app's `ColorSelect`** — this package must
 * stay WebView-hostable and may not import from the app. It is deliberately
 * small: a swatch drawn from the injected `resolveToken`, beside a native
 * select naming the token. A native `<option>` cannot reliably carry a colour
 * (Safari ignores its background), so the swatch is the one place the colour
 * is shown, and it always shows the colour the cell will actually take.
 *
 * It replaced two free-text boxes that wanted a token typed exactly —
 * `chart.down` — which nobody could do without the theme file open.
 *
 * A value not in the list (a hex, or a token from an older screen) is kept as
 * an extra option rather than silently reset, so opening the editor never
 * changes a rule.
 */

import { FORMAT_TOKENS } from "../lib/format-rules.js";

export function TokenSelect({
  label,
  value,
  onChange,
  resolve,
}: {
  label: string;
  value: string | undefined;
  onChange: (token: string | undefined) => void;
  resolve: (token: string) => string;
}) {
  const known = FORMAT_TOKENS.some((t) => t.token === value);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      <span
        aria-hidden
        style={{
          width: 11,
          height: 11,
          borderRadius: 2,
          border: "1px solid var(--qf-border)",
          background: value ? resolve(value) : "transparent",
        }}
      />
      <select
        aria-label={label}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        style={SELECT}
      >
        <option value="">none</option>
        {FORMAT_TOKENS.map((t) => (
          <option key={t.token} value={t.token}>
            {t.label}
          </option>
        ))}
        {value && !known && <option value={value}>{value}</option>}
      </select>
    </span>
  );
}

const SELECT: React.CSSProperties = {
  padding: "0.15rem 0.3rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.76rem",
};
